import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';

import { api, queryString } from '@/lib/api';
import type { NotificationListResponse, NotificationView } from '@shared/schemas/notification';

/*
 * The notification tray — ROADMAP.md §11.1, and where P7 is finally closed.
 *
 * The prototype fetched `/api/notifications` three to four times per page load
 * and again on every navigation. Here there is exactly one query per portal,
 * shared by the bell badge and the open tray — `unreadCount` rides on the list
 * response so they cannot become two requests — and the query lives in the
 * layout's topbar, which never remounts on navigation.
 *
 * `basePath` is the portal's API prefix. The tray is one control in three
 * portals; the prefix is the only thing that differs.
 */

const PAGE_SIZE = 15;

export type NotificationsBasePath = '/api/admin' | '/api/employee' | '/api/client';

export type NotificationFilter = 'all' | 'unread';

function listKey(basePath: NotificationsBasePath, filter: NotificationFilter) {
  return ['notifications', basePath, filter] as const;
}

export function useNotifications(basePath: NotificationsBasePath, filter: NotificationFilter) {
  return useInfiniteQuery({
    queryKey: listKey(basePath, filter),
    queryFn: ({ pageParam }) =>
      api.get<NotificationListResponse>(
        `${basePath}/notifications${queryString({ page: pageParam, pageSize: PAGE_SIZE, filter })}`,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    /*
     * A minute, deliberately longer than the 30s default. Notifications are
     * the one query on every screen of every portal; window focus still
     * refetches, so coming back to the tab is what refreshes the badge.
     */
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

/** The badge's number: unread across the account, from whichever page is freshest. */
export function unreadCountOf(
  data: InfiniteData<NotificationListResponse> | undefined,
): number | null {
  const first = data?.pages[0];
  return first ? first.unreadCount : null;
}

export function useMarkNotificationRead(basePath: NotificationsBasePath) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<NotificationView>(`${basePath}/notifications/${id}/read`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', basePath] });
    },
  });
}

export function useMarkAllNotificationsRead(basePath: NotificationsBasePath) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ marked: number }>(`${basePath}/notifications/read-all`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', basePath] });
    },
  });
}
